/**
 * FormMixin
 *
 * Common props/behavior/events for forms.
 *
 * See common-gokr forms for reference implementation.
 */
export default {
	emits: [
		"formChange",
		"focusChange",
	],
	props: {
		formId: {
			type: String,
			required: true,
		},
		revision: {
			type: Number,
			default: 0,
		},
		/** Is the form busy? */
		busy: {
			type: Boolean,
			default: false,
		},
		/** Is the entire form disabled? overrides disabledFields. **/
		disabled: {
			type: Boolean,
			default: false,
		},
		/**
		 * Are particular fields disabled?
		 * @type Object.<fieldName:String, Boolean>
		 **/
		disabledFields: {
			type: Object,
			default: () => ( {} ),
		},
		/**
		 * Are particular fields hidden?
		 * @type Object.<fieldName:String, Boolean>
		 **/
		hiddenFields: {
			type: Object,
			default: () => ( {} ),
		},
		/** @type String - formId.fieldName:startPosition **/
		focusedField: {
			type: String,
			default: "",
		},
		/** @type Object.<fieldName:String, error:String> */
		errors: {
			type: Object,
			default: () => {},
		},
	},
	computed: {
		focusedFieldParts() {
			if ( !this.focusedField ) {
				return {
					formId: "",
					fieldName: "",
					selectionStart: 0,
				};
			}
			const [ formId, fieldDefinition, ] = this.focusedField.split( "." );
			const [ fieldName, selectionStart, ] = fieldDefinition.split( ":" );
			return {
				formId,
				fieldName,
				selectionStart: parseInt( selectionStart, 10 ),
			};
		},
		/** @abstract */
		form() {
			return {};
		},
	},
	methods: {
		errorFor( fieldName ) {
			return {
				message: ( this.errors[ fieldName ] || "" ),
			};
		},
		isFocused( fieldName ) {
			const { focusedFieldParts, } = this;
			return focusedFieldParts.formId === this.formId &&
				focusedFieldParts.fieldName === fieldName;
		},
		focusedSelectionFor( fieldName ) {
			if ( !this.isFocused( fieldName ) ) {
				return [];
			}
			const { focusedFieldParts, } = this;
			return [ parseInt( focusedFieldParts.selectionStart, 10 ), ];
		},
		async raiseFocusChange( fieldName, selectionStart = 0 ) {
			const {
				fieldName: currentFieldName,
				selectionStart: currentSelectionStart,
			} = this.focusedFieldParts;

			if ( currentFieldName === fieldName && currentSelectionStart === selectionStart ) {
				return;
			}

			const { formId, } = this;

			await this.$nextTick();

			this.$emit( "focusChange", {
				formId,
				focusedFieldName: fieldName,
				selectionStart,
			} );
		},
		async raiseFormChange( patch = {}, force = false ) {
			const { form, } = this;

			const isFormChange = Object.keys( patch ).reduce( ( isChange, key ) => {
				return isChange || ( patch[ key ] !== form[ key ] );
			}, false );

			if ( !( force || isFormChange ) ) {
				return;
			}

			await this.$nextTick();

			this.$emit( "formChange", {
				...form,
				...patch,
			} );
		},
	},
};
